/* eslint-disable no-restricted-syntax, no-use-before-define */
import { ref, computed, readonly } from 'vue';
import { useAlert } from 'dashboard/composables';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { useStore } from 'dashboard/composables/store';
import {
  normalizeCampaignStatus,
  CAMPAIGN_STATUS,
} from 'dashboard/routes/dashboard/campaigns/helper/campaignStatus';

let campaignProgressInstance = null;

function createCampaignProgressInstance() {
  const store = useStore();

  const campaignProgress = ref({});
  const activeCampaigns = ref(new Set());
  const alertedStatuses = ref(new Set());
  const isInitialized = ref(false);

  const loadPersistedData = schedulingId => {
    const campaigns = store.state.campaigns.records;

    for (const campaign of campaigns) {
      if (campaign.campaign_schedules) {
        const schedule = campaign.campaign_schedules.find(
          s => s.id === schedulingId
        );
        if (schedule && schedule.status_data) {
          // Contrato: status_data.status / campaign_status / campaign_status_raw
          // já vêm CRU (enum). Sem mapa PT→EN.
          const mappedStatus = normalizeCampaignStatus(
            schedule.status_data.status ||
              schedule.campaign_status ||
              schedule.campaign_status_raw
          );

          // Priorizar status_data (Redis) sobre colunas database (podem estar desatualizadas)
          const newData = {
            progress:
              schedule.status_data.progress ||
              schedule.progress_percentage ||
              0,
            status: mappedStatus,
            totalItems:
              schedule.status_data.total_items || schedule.total_items || 0,
            currentItem:
              schedule.status_data.items_sent || schedule.items_sent || 0,
            itemsFailed:
              schedule.status_data.items_failed || schedule.items_failed || 0,
            message:
              schedule.status_data.message || schedule.status_message || '',
            statistics: schedule.statistics || null,
          };

          // Detectar mudança de status (WebSocket pode ter falhado)
          const currentData = campaignProgress.value[schedulingId];
          if (
            currentData &&
            currentData.status === 'running' &&
            mappedStatus !== 'running'
          ) {
            if (mappedStatus === 'completed') {
              useAlert('Campanha concluída!', 'success');
            } else if (mappedStatus === 'failed') {
              useAlert('Campanha falhou', 'error');
            } else if (mappedStatus === 'cancelled') {
              useAlert('Campanha cancelada', 'warning');
            }
          }

          campaignProgress.value[schedulingId] = newData;

          // Atualizar activeCampaigns
          if (mappedStatus === 'running') {
            activeCampaigns.value.add(schedulingId);
          } else {
            activeCampaigns.value.delete(schedulingId);
          }

          return newData;
        }
      }
    }

    return null;
  };

  const syncStatusWithStore = (
    schedulingId,
    status,
    statusLabel = null,
    statusData = null
  ) => {
    try {
      store.dispatch('campaigns/updateCampaignStatus', {
        scheduleId: schedulingId,
        status,
        statusLabel,
        statusData,
      });
    } catch {
      // Erro ao sincronizar com store
    }
  };

  const showAlertOnce = (schedulingId, status, message, alertType = 'info') => {
    const alertKey = `${schedulingId}-${status}`;

    const shouldShowAlert = [
      CAMPAIGN_STATUS.FAILED,
      CAMPAIGN_STATUS.CANCELLED,
    ].includes(status);

    if (shouldShowAlert && !alertedStatuses.value.has(alertKey)) {
      alertedStatuses.value.add(alertKey);

      if (status === CAMPAIGN_STATUS.FAILED) {
        useAlert(message, 'error');
      } else if (status === CAMPAIGN_STATUS.CANCELLED) {
        useAlert(message, 'warning');
      } else {
        useAlert(message, alertType);
      }

      setTimeout(() => {
        alertedStatuses.value.delete(alertKey);
      }, 5000);
    }
  };

  const applyCampaignStatusUpdate = data => {
    if (!data.schedule?.id) {
      return;
    }

    const schedulingId = data.schedule.id;
    // Contrato: data.status já vem CRU (enum); data.status_label é o rótulo
    // traduzido pelo servidor.
    const mappedStatus = normalizeCampaignStatus(data.status);
    const statusLabel = data.status_label || null;

    if (!campaignProgress.value[schedulingId]) {
      const persistedData = loadPersistedData(schedulingId);
      if (!persistedData) {
        campaignProgress.value[schedulingId] = {
          progress: 0,
          status: null,
          totalItems: 0,
          currentItem: 0,
          message: '',
          statistics: null,
        };
      }
    }

    const currentProgress = campaignProgress.value[schedulingId];
    let { totalItems, currentItem, progress, statistics } = currentProgress;

    // Em fan-out paralelo, ActionCable nao garante ordem de entrega entre
    // broadcasts de workers diferentes. Quando o status e running e chega
    // um update com items_sent < current OU progress < current, ignoramos:
    // e um snapshot atrasado de outro worker. Em status final
    // (completed/failed/cancelled) liberamos pra fechar 100%.
    const isFinalStatus = ['completed', 'failed', 'cancelled'].includes(
      mappedStatus
    );
    // Broadcast da RECONCILIAÇÃO (carimbo counters_reconciled_at nas
    // statistics) é autoritativo: zero ali é valor consolidado do ledger
    // (ex.: campanha 100% rejeitada → items_sent=0), não reset espúrio de
    // worker — os guards de zero abaixo não se aplicam a ele.
    const isReconciledUpdate = Boolean(
      data.message?.statistics?.counters_reconciled_at
    );
    const incomingSent = data.message?.items_sent;
    const incomingProgress =
      data.message?.progress !== undefined
        ? parseFloat(data.message.progress)
        : null;
    // Terminal sticky: se o status atual ja eh terminal, ignora qualquer
    // update 'running' que chegue depois (broadcast direto sem stream pode
    // entregar fora de ordem; sem essa defesa o badge volta de "Concluido"
    // pra "Em execucao").
    const currentStatusValue = campaignProgress.value[schedulingId]?.status;
    const currentIsTerminal = ['completed', 'failed', 'cancelled'].includes(
      currentStatusValue
    );

    // RE-EXECUÇÃO: o backend zera counters/colunas ao entrar em :running.
    // Um update 'running' cujo timestamp é MAIS NOVO que o evento terminal
    // (ou que chega com o snapshot local ainda de outra execução — status
    // scheduled/terminal) é execução nova, não snapshot atrasado de worker.
    // Sem este reset, os guards abaixo descartariam a 2ª execução INTEIRA
    // (progress < 100 e sent < currentItem antigos do singleton).
    const eventAt = Date.parse(data.timestamp || '') || Date.now();
    const isRestart =
      mappedStatus === 'running' &&
      currentStatusValue !== 'running' &&
      eventAt > (currentProgress.terminalAt || 0);

    if (isRestart) {
      currentProgress.itemsFailed = 0;
      currentProgress.message = '';
      currentProgress.statistics = null;
      totalItems = 0;
      currentItem = 0;
      progress = 0;
      statistics = null;
    }

    const isStaleUpdate =
      !isRestart &&
      !isFinalStatus &&
      mappedStatus === 'running' &&
      (currentIsTerminal ||
        (incomingSent !== undefined &&
          currentItem > 0 &&
          incomingSent < currentItem) ||
        (incomingProgress !== null &&
          progress > 0 &&
          incomingProgress < progress));
    if (isStaleUpdate) {
      return;
    }

    let statusDataForStore = null;
    if (typeof data.message === 'object' && data.message !== null) {
      if (data.message.total_items) {
        totalItems = data.message.total_items;
      }

      // currentItem NUNCA é derivado de progress×totalItems: o broadcast de
      // erro traz progress sem items_sent, e a derivação inflava "enviados"
      // com o total PROCESSADO (falhas incluídas), travando o isStaleUpdate
      // contra os broadcasts reais seguintes. Sem items_sent no payload,
      // currentItem simplesmente não muda.
      if (data.message.items_sent !== undefined) {
        // Proteger contra reset de zeros quando já temos progresso real —
        // EXCETO no broadcast reconciliado, cujo zero é o consolidado real.
        const isZeroReset = data.message.items_sent === 0 && currentItem > 0;
        const isRunningWithProgress =
          mappedStatus === 'running' && (currentItem > 0 || progress > 0);

        if (
          isReconciledUpdate ||
          !(isZeroReset && (isFinalStatus || isRunningWithProgress))
        ) {
          currentItem = data.message.items_sent;
          progress = totalItems > 0 ? (currentItem / totalItems) * 100 : 0;
        }
      }

      if (data.message.progress !== undefined) {
        const newProgress = parseFloat(data.message.progress);
        // Proteger contra reset de progress=0 quando já temos progresso real
        if (
          !(newProgress === 0 && progress > 0 && mappedStatus === 'running')
        ) {
          progress = newProgress;
        }
      }

      // Atualizar itemsFailed
      if (data.message.items_failed !== undefined) {
        // Counter Redis é monotônico durante running; broadcast de erro do
        // backend manda delta local (zerado pelo flush) e pode REGREDIR o
        // acumulado — mesmo racional do isStaleUpdate, que só cobre sent.
        const regressedWhileRunning =
          mappedStatus === 'running' &&
          data.message.items_failed < (currentProgress.itemsFailed || 0);
        const zeroResetOnFinal =
          (mappedStatus === 'completed' || mappedStatus === 'cancelled') &&
          data.message.items_failed === 0 &&
          currentProgress.itemsFailed > 0;

        if (
          isReconciledUpdate ||
          (!regressedWhileRunning && !zeroResetOnFinal)
        ) {
          currentProgress.itemsFailed = data.message.items_failed;
        }
      }

      // Merge das statistics: Só atualiza se vier com dados válidos
      if (data.message.statistics) {
        if (Object.keys(data.message.statistics).length > 0) {
          statistics = {
            ...(currentProgress.statistics || {}),
            ...data.message.statistics,
          };
        } else {
          statistics = currentProgress.statistics || null;
        }
      } else if (!statistics) {
        statistics = currentProgress.statistics || null;
      }

      currentProgress.message = data.message.message || '';

      statusDataForStore = {
        // status_data.status agora é CRU (enum), consistente com o resto do contrato
        status: mappedStatus,
        status_label: statusLabel,
        message: data.message.message,
        progress,
        total_items: totalItems,
        // Campos ausentes seguem undefined de propósito — o `|| 0` antigo
        // zerava o registro no store quando o broadcast terminal vinha sem
        // items_* (o updateCampaignStatus só grava o que veio de fato).
        items_processed: data.message.items_processed,
        items_sent: data.message.items_sent,
        items_failed: data.message.items_failed,
        current_batch: data.message.current_batch,
        total_batches: data.message.total_batches,
        statistics,
        last_updated_at: new Date().toISOString(),
      };

      const messageText = data.message.message || '';
      if (
        messageText.includes('Campanha iniciada com') ||
        messageText.includes('Campaign started with')
      ) {
        const totalMatch = messageText.match(
          /(?:Campanha iniciada com|Campaign started with) (\d+) (?:itens para processar|items to process)/
        );
        if (totalMatch) {
          totalItems = parseInt(totalMatch[1], 10);
          currentItem = 0;
          progress = 0;
          activeCampaigns.value.add(schedulingId);
        }
      }
    }

    const oldStatus = currentProgress.status;
    const statusChanged = oldStatus !== mappedStatus;

    if (statusChanged) {
      if (mappedStatus === CAMPAIGN_STATUS.COMPLETED) {
        // NÃO fabricar progress=100/currentItem=totalItems aqui: o broadcast
        // terminal já traz o items_sent REAL e o progress do banco — a
        // fabricação mostrava "tudo enviado" em campanha cortada por limite
        // diário, cancel ou holds.
        activeCampaigns.value.delete(schedulingId);
      } else if (mappedStatus === CAMPAIGN_STATUS.FAILED) {
        activeCampaigns.value.delete(schedulingId);
        showAlertOnce(
          schedulingId,
          CAMPAIGN_STATUS.FAILED,
          'Erro durante a execução da campanha.',
          'error'
        );
      } else if (mappedStatus === CAMPAIGN_STATUS.CANCELLED) {
        activeCampaigns.value.delete(schedulingId);
        showAlertOnce(
          schedulingId,
          CAMPAIGN_STATUS.CANCELLED,
          'Campanha cancelada com sucesso',
          'warning'
        );
      } else if (mappedStatus === CAMPAIGN_STATUS.RUNNING) {
        activeCampaigns.value.add(schedulingId);
      }

      syncStatusWithStore(
        schedulingId,
        mappedStatus,
        statusLabel,
        statusDataForStore
      );
    }

    const finalState = {
      progress: Math.min(Math.max(parseFloat(progress) || 0, 0), 100),
      status: mappedStatus,
      totalItems,
      currentItem,
      itemsFailed: currentProgress.itemsFailed || 0,
      message: currentProgress.message,
      statistics,
      // Marca temporal do evento terminal — a detecção de re-execução
      // compara o timestamp do broadcast 'running' com ela.
      terminalAt: isFinalStatus ? eventAt : currentProgress.terminalAt || null,
    };

    campaignProgress.value[schedulingId] = finalState;
  };

  // Throttle leading+trailing 200ms — backend agora envia broadcasts a cada 5s ou
  // 100 records, mas mesmo assim em rajadas curtas (final do batch, mudança de
  // status) podem chegar várias mensagens em <200ms. Garantimos que processamos
  // a primeira e a última de cada janela; intermediárias são descartadas.
  const handleCampaignStatusUpdated = (() => {
    const THROTTLE_MS = 200;
    let lastCall = 0;
    let pendingTimer = null;
    let pendingArgs = null;

    return data => {
      pendingArgs = data;
      const now = Date.now();
      const elapsed = now - lastCall;

      if (elapsed >= THROTTLE_MS) {
        lastCall = now;
        applyCampaignStatusUpdate(data);
      } else if (!pendingTimer) {
        pendingTimer = setTimeout(() => {
          lastCall = Date.now();
          pendingTimer = null;
          applyCampaignStatusUpdate(pendingArgs);
        }, THROTTLE_MS - elapsed);
      }
    };
  })();

  const initializeListeners = () => {
    if (isInitialized.value) {
      return;
    }

    emitter.on(BUS_EVENTS.CAMPAIGN_STATUS_UPDATED, handleCampaignStatusUpdated);

    isInitialized.value = true;
  };

  const cleanup = () => {
    emitter.off(
      BUS_EVENTS.CAMPAIGN_STATUS_UPDATED,
      handleCampaignStatusUpdated
    );
    isInitialized.value = false;
  };

  const getProgressForScheduling = schedulingId => {
    return computed(() => {
      let data = campaignProgress.value[schedulingId];

      if (!data) {
        const persistedData = loadPersistedData(schedulingId);
        if (persistedData) {
          data = persistedData;
        } else {
          data = {
            progress: 0,
            status: null,
            totalItems: 0,
            currentItem: 0,
            message: '',
            statistics: null,
          };
        }
      }

      return data;
    });
  };

  const getProgressPercentage = schedulingId => {
    return computed(() => {
      const progressVal = campaignProgress.value[schedulingId];
      if (!progressVal) {
        const persistedData = loadPersistedData(schedulingId);
        return persistedData ? Math.round(persistedData.progress) : 0;
      }
      return Math.round(progressVal.progress);
    });
  };

  const isActive = schedulingId => {
    return computed(() => {
      if (activeCampaigns.value.has(schedulingId)) {
        return true;
      }

      const campaigns = store.state.campaigns.records;
      for (const campaign of campaigns) {
        if (campaign.campaign_schedules) {
          const schedule = campaign.campaign_schedules.find(
            s => s.id === schedulingId
          );
          if (
            schedule &&
            (schedule.campaign_status_raw === CAMPAIGN_STATUS.RUNNING ||
              schedule.campaign_status === CAMPAIGN_STATUS.RUNNING)
          ) {
            activeCampaigns.value.add(schedulingId);
            return true;
          }
        }
      }

      return false;
    });
  };

  const getAllActiveCampaigns = computed(() => {
    return Array.from(activeCampaigns.value);
  });

  const startCampaign = async (campaignId, schedulingId) => {
    await store.dispatch('campaigns/startCampaign', {
      campaignId,
      schedulingId,
    });
  };

  const stopCampaign = async (campaignId, schedulingId) => {
    await store.dispatch('campaigns/stopCampaign', {
      campaignId,
      schedulingId,
    });
  };

  const clearCampaignData = schedulingId => {
    delete campaignProgress.value[schedulingId];
    activeCampaigns.value.delete(schedulingId);

    const keysToDelete = Array.from(alertedStatuses.value).filter(key =>
      key.startsWith(`${schedulingId}-`)
    );
    keysToDelete.forEach(key => alertedStatuses.value.delete(key));
  };

  const resetCampaignProgress = (schedulingId, newStatus) => {
    if (campaignProgress.value[schedulingId]) {
      campaignProgress.value[schedulingId] = {
        progress: 0,
        status: newStatus,
        totalItems: 0,
        currentItem: 0,
        message: '',
        statistics: null,
      };
    }
  };

  const clearAllCampaigns = () => {
    campaignProgress.value = {};
    activeCampaigns.value.clear();
    alertedStatuses.value.clear();
  };

  const globalCleanup = () => {
    cleanup();
    clearAllCampaigns();
  };

  return {
    campaignProgress: readonly(campaignProgress),
    activeCampaigns: readonly(activeCampaigns),
    isInitialized: readonly(isInitialized),

    getProgressForScheduling,
    getProgressPercentage,
    isActive,
    getAllActiveCampaigns,

    startCampaign,
    stopCampaign,
    clearCampaignData,
    resetCampaignProgress,
    clearAllCampaigns,
    loadPersistedData,

    handleCampaignStatusUpdated,

    initializeListeners,
    cleanup,
    globalCleanup,
  };
}

export function useCampaignProgress() {
  if (!campaignProgressInstance) {
    campaignProgressInstance = createCampaignProgressInstance();
  }

  if (!campaignProgressInstance.isInitialized.value) {
    campaignProgressInstance.initializeListeners();
  }

  return campaignProgressInstance;
}

export function cleanupCampaignProgress() {
  if (campaignProgressInstance) {
    campaignProgressInstance.globalCleanup();
    campaignProgressInstance = null;
  }
}
